import { type NormalizedSchema, normalize } from 'normalizr';

import type { BaseAppState } from '../types';
import {
  createEntityDenormalizer,
  type EntityDenormalizer,
} from './normalizers';
import { entitySchemas } from './schemas';
import type {
  EntitiesMap,
  NormalizedEntitiesMap,
  StoreEntityKey,
} from './types';

type Result<
  Entity extends EntitiesMap[EntityType],
  EntityType extends StoreEntityKey,
  Object extends Partial<Entity> | Partial<Entity>[],
> = Object extends Partial<Entity>[]
  ? NormalizedSchema<
      { [key in StoreEntityKey]?: Record<string, NormalizedEntitiesMap[key]> },
      string[]
    >
  : NormalizedSchema<
      { [key in StoreEntityKey]?: Record<string, NormalizedEntitiesMap[key]> },
      string
    >;

export const normalizeEntities = <
  Entity extends EntitiesMap[EntityType],
  EntityType extends StoreEntityKey,
  Object extends Partial<Entity> | Partial<Entity>[],
>(
  object: Object,
  entityType: EntityType,
): Result<Entity, EntityType, Object> => {
  const entitySchema = entitySchemas[entityType];
  if (!entitySchema) {
    throw new Error(
      `No schema found in entitySchemas for entity type: ${entityType}`,
    );
  }
  if (Array.isArray(object)) {
    // biome-ignore lint/plugin: normalizr types normalize() loosely; Result encodes the entity and array-ness this call site already knows.
    return normalize(object, [entitySchema]) as Result<
      Entity,
      EntityType,
      Object
    >;
  }
  // biome-ignore lint/plugin: normalizr types normalize() loosely; Result encodes the entity and array-ness this call site already knows.
  return normalize(object, entitySchema) as Result<Entity, EntityType, Object>;
};

const denormalizerCache = new Map<
  StoreEntityKey,
  ReturnType<typeof createEntityDenormalizer>
>();

export const getDenormalizedEntityById = <EntityType extends StoreEntityKey>(
  state: BaseAppState,
  entityType: EntityType,
  id: string | null | undefined,
): EntitiesMap[EntityType] | undefined => {
  if (!id) {
    return undefined;
  }
  // biome-ignore lint/plugin: the cache is keyed by entity type but stores a union of denormalizers, so the lookup cannot stay generic.
  let denormalizeEntity = denormalizerCache.get(
    entityType,
  ) as EntityDenormalizer<EntityType>;

  if (!denormalizeEntity) {
    denormalizeEntity = createEntityDenormalizer(entityType);
    denormalizerCache.set(entityType, denormalizeEntity);
  }

  return denormalizeEntity(state.entities, id);
};

export const getEntityById = <EntityType extends StoreEntityKey>(
  state: BaseAppState,
  entityType: EntityType,
  id: string | null | undefined,
): NormalizedEntitiesMap[EntityType] | undefined => {
  if (!id) {
    return undefined;
  }
  return state.entities[entityType][id];
};
